import { useEffect, useRef, useState } from 'react';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import styles from '../styles/Projects.module.css';

const PROJECTS = [
  { slug: 'aguileras-home-repair', name: "Aguilera's Home Repair", blurb: 'Home repair, remodeling & plumbing — Acworth, GA', url: '' },
  { slug: 'yates-services-memphis', name: 'Yates Services of Memphis', blurb: 'HVAC & electrical contractor — Memphis, TN', url: '' },
  { slug: 'santos-landscape', name: 'Santos Landscape', blurb: 'Landscaping & tree services', url: '' },
  { slug: 'top-notch-drywall-painting', name: 'Top Notch Drywall & Painting', blurb: 'Drywall & painting contractor — Peoria, IL', url: '' },
  { slug: 'vip-renovation-philadelphia', name: 'VIP Renovation', blurb: 'Home renovation experts — Philadelphia, PA', url: '' },
  { slug: 'radiant-ritual-spa', name: 'Radiant Ritual', blurb: 'Spa & salon booking site', url: '' },
  { slug: 'petes-dumpster-rental', name: "Pete's Dumpster Rental", blurb: 'Dumpster rentals — Treasure Valley, ID', url: '' },
  { slug: 'mulched-seed-yard-care', name: 'Mulched Seed Yard Care', blurb: 'Yard care & landscaping — Denver, CO', url: '' },
  { slug: 'mfm-designs', name: 'MFM Designs', blurb: 'Custom comic book & manga art commissions', url: '' },
  { slug: 'mental-forge-ai', name: 'Mental Forge AI', blurb: 'AI automation systems for North Texas businesses', url: '' },
  { slug: 'jedi-atlantis', name: 'Jedi Atlantis', blurb: 'Sci-fi comic reading platform', url: '' },
  { slug: 'happy-homes-nm', name: 'Happy Homes', blurb: 'Lath, plaster & roofing contractor — New Mexico', url: '' },
  { slug: 'global-desk-news', name: 'Global Desk News', blurb: 'Multi-category news portal', url: '' },
  { slug: 'elevate-vr-idaho', name: 'Elevate VR', blurb: 'VR gaming trailer experience — Idaho', url: '' },
  { slug: 'carpet-repair-nevada-1', name: 'Carpet Repair Nevada', blurb: 'Carpet repair specialists — Reno / Sparks / Carson City', url: '' },
  { slug: 'baldovinos-construction', name: 'Baldovinos General Construction', blurb: 'General contractor — San Fernando Valley, CA', url: '' },
  { slug: 'dana-mcguffin-cpa', name: 'Dana McGuffin CPA', blurb: 'Bookkeeping & accounting services — Southlake, TX', url: '' },
  { slug: 'carpet-repair-nevada-2', name: 'Carpet Repair Nevada', blurb: 'Carpet repair specialists — Reno / Sparks / Carson City', url: '' },
  { slug: 'atlas-learners', name: 'Atlas Learners', blurb: 'Online course & eLearning platform', url: '' },
  { slug: 'mental-forge-media', name: 'Mental Forge Media', blurb: 'Mobile podcasting, theatre & BPO services', url: '' },
  { slug: 'topfreetools', name: 'TopFreeTools', blurb: 'Free online calculators & web tools', url: '' },
  { slug: 'versetechhub', name: 'VerseTechHub', blurb: 'AI tools reviews & blog', url: '' },
  { slug: 'saascomparely', name: 'SaaS Comparely', blurb: 'SaaS product comparison blog', url: '' },
  { slug: 'fitroutine', name: 'FitRoutine', blurb: 'Fitness & workout blog', url: '' },
  { slug: 'glynn-associates', name: 'Glynn Associates', blurb: 'Government relations & public policy — Massachusetts', url: '' },
  { slug: 'bonney-building', name: 'Bonney Building', blurb: 'General contractor — Cape Cod, MA', url: '' },
  { slug: 'mevteck', name: 'Mevteck', blurb: 'Digital marketing agency — Karachi, Pakistan', url: '' },
];

const PAGE_SIZE = 6;
const SCROLL_SPEED = 320;
const RETURN_SPEED = 700;

function ProjectCard({ project, animate, onOpen }) {
  const viewportRef = useRef(null);
  const imgRef = useRef(null);
  const distanceRef = useRef(0);

  const handleEnter = () => {
    const viewport = viewportRef.current;
    const img = imgRef.current;
    if (!viewport || !img) return;
    const distance = img.offsetHeight - viewport.offsetHeight;
    if (distance <= 0) return;
    distanceRef.current = distance;
    const duration = Math.min(20, Math.max(7, distance / SCROLL_SPEED));
    img.style.transitionDuration = `${duration}s`;
    img.style.transitionTimingFunction = 'ease-in-out';
    img.style.transform = `translateY(-${distance}px)`;
  };

  const handleLeave = () => {
    const img = imgRef.current;
    if (!img) return;
    const distance = distanceRef.current;
    const duration = distance > 0 ? Math.min(4, Math.max(1, distance / RETURN_SPEED)) : 0.8;
    img.style.transitionDuration = `${duration}s`;
    img.style.transitionTimingFunction = 'ease';
    img.style.transform = 'translateY(0)';
  };

  return (
    <div className={`${styles.card} ${animate ? styles.cardEnter : ''}`} data-project>
      <button type="button" className={styles.cardButton} onClick={() => onOpen(project)}>
        <div className={styles.browserBar}>
          <span className={styles.dots}>
            <i className={styles.dotRed} />
            <i className={styles.dotYellow} />
            <i className={styles.dotGreen} />
          </span>
          <span className={styles.urlPill}>{project.name}</span>
        </div>
        <div className={styles.shotViewport} ref={viewportRef} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
          <img ref={imgRef} src={`/projects/${project.slug}.jpg`} alt={project.name} loading="lazy" />
        </div>
        <div className={styles.cardFoot}>
          <h3 className={styles.name}>{project.name}</h3>
          <p className={styles.blurb}>{project.blurb}</p>
        </div>
      </button>
    </div>
  );
}

export default function Projects() {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return undefined;
    document.body.style.overflow = 'hidden';
    ScrollSmoother.get()?.paused(true);
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      ScrollSmoother.get()?.paused(false);
      window.removeEventListener('keydown', onKey);
    };
  }, [lightbox]);

  const visibleProjects = PROJECTS.slice(0, visibleCount);

  return (
    <section id="projects" className="section">
      <div className="container">
        <div className={styles.head}>
          <div className={styles.headLeft} data-anim>
            <span className="section-index">06</span>
            <span className="section-rule" />
            <h2 className="section-title">Selected Work</h2>
          </div>
          <span className={styles.count}>{PROJECTS.length.toString().padStart(2, '0')} projects</span>
        </div>

        <div className={styles.grid}>
          {visibleProjects.map((project, i) => (
            <ProjectCard key={project.slug} project={project} animate={i >= PAGE_SIZE} onOpen={setLightbox} />
          ))}
        </div>

        {visibleCount < PROJECTS.length && (
          <div className={styles.loadMoreWrap}>
            <button
              type="button"
              className={styles.loadMore}
              onClick={() => setVisibleCount((c) => Math.min(c + PAGE_SIZE, PROJECTS.length))}
            >
              Load More
            </button>
          </div>
        )}
      </div>

      {lightbox && (
        <div className={styles.lightboxOverlay} onClick={() => setLightbox(null)}>
          <div className={styles.lightboxFrame} onClick={(e) => e.stopPropagation()}>
            <div className={styles.lightboxHead}>
              <span>{lightbox.name}</span>
              <button type="button" className={styles.lightboxClose} onClick={() => setLightbox(null)} aria-label="Close">
                ×
              </button>
            </div>
            <div className={styles.lightboxScroll}>
              <img src={`/projects/${lightbox.slug}.jpg`} alt={lightbox.name} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
